import React, { useState, useEffect } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { FaFileCode, FaFilePdf } from 'react-icons/fa';
import UploadResume from '../JobComponents/UploadResume';
import SectionHeader from '../Pages/Components/SectionHeader';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import RawJsonEditor from '../Utils/RawJsonEditor';

// Import ProfileShards components
import HardSkills from '../CandidateComponents/ProfileShards/HardSkills';
import SoftSkills from '../CandidateComponents/ProfileShards/SoftSkills';
import Expertise from '../CandidateComponents/ProfileShards/Expertise';
import WorkExperience from '../CandidateComponents/ProfileShards/WorkExperience';
import Projects from '../CandidateComponents/ProfileShards/Projects';
import HonorsAndAwards from '../CandidateComponents/ProfileShards/HonorsAndAwards';
import PersonalInfo from '../CandidateComponents/ProfileShards/PersonalInfo';
import Bio from '../CandidateComponents/ProfileShards/Bio';
import Education from '../CandidateComponents/ProfileShards/Education';
import Certifications from '../CandidateComponents/ProfileShards/Certifications';
import Languages from '../CandidateComponents/ProfileShards/Languages';

const TalentProfile = ({ talent }) => {
  const { user } = useSession();
  // State for upload resume popup
  const [isUploadResumeOpen, setIsUploadResumeOpen] = useState(false);
  // State for raw edit talent profile popup
  const [isRawEditOpen, setIsRawEditOpen] = useState(false);
  
  // Extract necessary data from talent prop
  const [talentData, setTalentData] = useState(null);
  const [talentProfileJSON, setTalentProfileJSON] = useState(null);
  
  useEffect(() => {
    if (talent) {
      // Extract basic talent data
      setTalentData({
        id: talent.id,
        uid: talent.uid,
        fname: talent.fname,
        lname: talent.lname,
        cvURL: talent.signedURL,
        secretForAssessment: talent.secretForAssessment || null,
      });
      
      // Extract talent profile JSON
      if (talent.talentProfileJSON) {
        setTalentProfileJSON(talent.talentProfileJSON);
        console.log('Talent Profile JSON loaded in TalentProfile component:', talent.talentProfileJSON);
      }
    }
  }, [talent]);
  // Helper function to format date if available
  const formatDate = (dateString) => {
    if (!dateString || dateString.includes("[")) return "N/A";
    return dateString;
  };

  // Check if we have valid profile data
  const hasProfileData = talentProfileJSON && Object.keys(talentProfileJSON).length > 0;
  
  // Use talentProfileJSON for the profile information
  const talentProfile = talentProfileJSON || {};
  
  // Determine if upload resume button should be shown
  // Show if: profile doesn't exist OR user is Superadmin
  const shouldShowUploadButton = !hasProfileData || user?.role === 'Superadmin';

  // Save function for talent profile
  const handleSaveTalentProfile = async (editedData) => {
    if (!talent?.id) {
      throw new Error('Talent ID is required to save talent profile');
    }

    console.log('Saving edited talent profile:', editedData);

    // Make API call to update talent profile
    const response = await fetch(API_ENDPOINTS.UPDATE_TALENT_PROFILE_JSON, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      },
      body: JSON.stringify({
        talent_id: talent.id,
        talent_profile_json: editedData
      })
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Talent profile updated successfully:', result);

    // Reload the page to refresh data
    window.location.reload();
  };

  return (
    <div>
      {/* Tab-related header */}
      <SectionHeader
        title="Talent Profile"
        description="This tab displays comprehensive information about the talent, including personal details, professional summary, skills, work experience, education, certifications, and languages."
        actions={[
          ...(talentProfileJSON ? [
            <button 
              key="rawEdit"
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors whitespace-nowrap flex items-center"
              onClick={() => setIsRawEditOpen(true)}
            >
              <FaFileCode className="mr-2" /> Raw JSON Edit
            </button>
          ] : []),
          ...(shouldShowUploadButton ? [
            <button 
              key="regenerate"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors whitespace-nowrap flex items-center"
              onClick={() => setIsUploadResumeOpen(true)}
            >
              <FiRefreshCw className="mr-2" /> Upload Resume (Regenerate)
            </button>
          ] : []),
          ...(talentProfileJSON ? [
            <button 
              key="getPdf"
              className="px-4 py-2 bg-gray-400 text-white rounded cursor-not-allowed opacity-70 whitespace-nowrap flex items-center"
              disabled
            >
              <FaFilePdf className="mr-2" /> PDF
            </button>
          ] : [])
        ]}
      />
      
      {!talentData || !hasProfileData ? (
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Talent profile data will be displayed here.</p>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6">
          {/* CV Column - 33% width */}
          <div className="w-full md:w-1/3">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resume</h3>
                {talentData.cvURL ? (
                  <div className="overflow-auto max-h-screen">
                    <iframe 
                      src={talentData.cvURL} 
                      className="w-full h-[800px] border-0"
                      title="Talent CV"
                    ></iframe>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No resume available for this talent.</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Profile Column - 67% width */}
          <div className="w-full md:w-2/3 space-y-8">
            {/* Personal Information */}
            <PersonalInfo candidateProfile={talentProfile} roleCategory={talent?.roleCategory} formatDate={formatDate} />

            {/* Bio */}
            <Bio bio={talentProfile.bio} />

            {/* Skills Section - Two rows with Hard Skills on top and Soft Skills below */}
            <div className="space-y-6">
              {/* Hard Skills */}
              <HardSkills hardSkills={talentProfile.hardSkills} />

              {/* Soft Skills */}
              <SoftSkills softSkills={talentProfile.softSkills} />
            </div>

            {/* Expertise */}
            <Expertise expertise={talentProfile.expertise} />

            {/* Work Experience */}
            <WorkExperience workExperiences={talentProfile.workExperiences} formatDate={formatDate} />

            {/* Projects */}
            <Projects projects={talentProfile.projects} />

            {/* Honors and Awards */}
            <HonorsAndAwards honorsAndAwards={talentProfile.honorsAndAwards} />

            {/* Education, Certifications, Languages */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Education education={talentProfile.education} />
              <Certifications certifications={talentProfile.certifications} />
              <Languages languages={talentProfile.languages} />
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Resume Popup */}
      <UploadResume
        isOpen={isUploadResumeOpen}
        onClose={() => setIsUploadResumeOpen(false)}
        onSubmit={() => {}} // Empty handler as per requirements
        talentId={talent?.id}
        context="talent"
      />
      
      {/* Raw Edit Talent Profile Modal */}
      <RawJsonEditor
        isOpen={isRawEditOpen}
        onClose={() => setIsRawEditOpen(false)}
        data={talentProfileJSON}
        title="Raw Edit Talent Profile"
        description="Edit the talent profile JSON directly. This is a temporary solution which will be replaced with a form."
        rootName="talentProfile"
        hiddenAttributes={['rationale', 'relevance', 'responsibilityList']}
        onSave={handleSaveTalentProfile}
        saveButtonText="Save"
      />
    </div>
  );
};

export default TalentProfile;
